import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { satelliteMachineService } from "../services/satelliteMachineService";
import { satelliteMachineDisplayId } from "./SatelliteMachineCommonStore";
import { SatelliteMachine } from "../../../api/graphql";
import { userDisplayId } from "../../user/store/UserCommonStore";
import { getSatelliteTaskValues } from "../../satelliteTask/store/SatelliteTaskCommonStore";
import { ColumnWidthInfo } from "../../common/services/crudServices";
import { CrudRows } from "../../common/types";
import { userService } from "../../user/services/userService";
import { InitializeParams } from "../../../store/types";

export class SatelliteMachineViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = satelliteMachineService;
  satelliteMachineInstance: SatelliteMachine | null = null;
  cardState: Record<string, boolean> = {
    identity: true,
    certificate: true,
    authentication: true,
    authorization: true,
    capabilities: true,
    network: true,
    monitoring: true,
    relations: true,
  };
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  nameValue: string = "";
  machineCodeValue: string = "";
  satelliteTypeValue: string = "";
  regionValue: string = "";
  certificateExpiresAtValue: Date | null = null;
  authTokenLastFourValue: string = "";
  authTokenCreatedAtValue: Date | null = null;
  authTokenRotatedAtValue: Date | null = null;
  statusValue: string = "";
  authorizedAtValue: Date | null = null;
  authorizedByService = userService;
  authorizedBySearchValue: string = "";
  authorizedByIdValue: string = "";
  authorizedBySelectedValue: { displayId: string; id: string } | null = null;
  suspendedAtValue: Date | null = null;
  suspendedByService = userService;
  suspendedBySearchValue: string = "";
  suspendedByIdValue: string = "";
  suspendedBySelectedValue: { displayId: string; id: string } | null = null;
  suspensionReasonValue: string = "";
  enabledValue: boolean = false;
  allowedTenantIdsValue: string = "";
  proxyEnabledValue: boolean = false;
  proxyPortValue: number | undefined = undefined;
  proxyProtocolsValue: string = "";
  botEnabledValue: boolean = false;
  botMaxConcurrentValue: number | undefined = undefined;
  workerEnabledValue: boolean = false;
  publicIpValue: string = "";
  locationValue: string = "";
  countryCodeValue: string = "";
  lastHeartbeatAtValue: Date | null = null;
  lastHeartbeatMetricsValue: string = "{}";
  connectedAtValue: Date | null = null;
  disconnectedAtValue: Date | null = null;
  tunnelConnectedAtValue: Date | null = null;
  tasksColumnWidths: Record<string, number | string> = {};
  tasksRows: CrudRows = {};

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;
    this.tasksColumnWidths = {
      taskType: 200,
      status: 200,
      queuedAt: 200,
      completedAt: 200,
      duration: 200,
    };

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadSatelliteMachineById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.satelliteMachineOne({ id });
    this.satelliteMachineInstance = result.satelliteMachineOne;
    if (this.satelliteMachineInstance) {
      this.loadValues(this.satelliteMachineInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.satelliteMachineInstance) {
      return satelliteMachineDisplayId(
        this.satelliteMachineInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.SatelliteMachineSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "authorizedBy") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.userOne({ id: params.returnData.resultId });
        const user = result.userOne;
        this.authorizedBySelectedValue = user;
      } else if (params.callerData?.fieldName === "suspendedBy") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.userOne({ id: params.returnData.resultId });
        const user = result.userOne;
        this.suspendedBySelectedValue = user;
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  get authorizedByValue() {
    if (this.authorizedBySelectedValue) {
      return {
        id: this.authorizedBySelectedValue.id,
        displayId: userDisplayId(
          this.authorizedBySelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  get suspendedByValue() {
    if (this.suspendedBySelectedValue) {
      return {
        id: this.suspendedBySelectedValue.id,
        displayId: userDisplayId(this.suspendedBySelectedValue, this.langStore),
      };
    }
    return null;
  }

  get tasksVisibleColumns() {
    return [
      {
        name: "taskType",
        title: this.langStore.SatelliteTaskTaskTypeLabel,
        width: this.tasksColumnWidths["taskType"],
      },
      {
        name: "status",
        title: this.langStore.SatelliteTaskStatusLabel,
        width: this.tasksColumnWidths["status"],
      },
      {
        name: "queuedAt",
        title: this.langStore.SatelliteTaskQueuedAtLabel,
        width: this.tasksColumnWidths["queuedAt"],
      },
      {
        name: "completedAt",
        title: this.langStore.SatelliteTaskCompletedAtLabel,
        width: this.tasksColumnWidths["completedAt"],
      },
      {
        name: "duration",
        title: this.langStore.SatelliteTaskDurationLabel,
        width: this.tasksColumnWidths["duration"],
      },
    ];
  }

  tasksSetColumnWiths(x: ColumnWidthInfo[]) {
    const values: Record<string, number | string> = {};
    for (const item of x) {
      values[item.columnName] = item.width;
    }
    this.tasksColumnWidths = values;
  }

  get tasksRowsDyn() {
    return this.tasksRows;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.satelliteMachineInstance = null;
    this.nameValue = "";
    this.machineCodeValue = "";
    this.satelliteTypeValue = "";
    this.regionValue = "";
    this.certificateExpiresAtValue = null;
    this.authTokenLastFourValue = "";
    this.authTokenCreatedAtValue = null;
    this.authTokenRotatedAtValue = null;
    this.statusValue = "";
    this.authorizedAtValue = null;
    this.authorizedBySearchValue = "";
    this.authorizedByIdValue = "";
    this.suspendedAtValue = null;
    this.suspendedBySearchValue = "";
    this.suspendedByIdValue = "";
    this.suspensionReasonValue = "";
    this.enabledValue = false;
    this.allowedTenantIdsValue = "";
    this.proxyEnabledValue = false;
    this.proxyPortValue = undefined;
    this.proxyProtocolsValue = "";
    this.botEnabledValue = false;
    this.botMaxConcurrentValue = undefined;
    this.workerEnabledValue = false;
    this.publicIpValue = "";
    this.locationValue = "";
    this.countryCodeValue = "";
    this.lastHeartbeatAtValue = null;
    this.lastHeartbeatMetricsValue = "{}";
    this.connectedAtValue = null;
    this.disconnectedAtValue = null;
    this.tunnelConnectedAtValue = null;
    this.tasksRows = {};
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelSatelliteMachine() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return this.rootStore.authStore.hasPermissions("satelliteMachine_update");
  }

  editSatelliteMachine() {
    const id = this.satelliteMachineInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/satelliteMachine/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printSatelliteMachine(handlePrint: () => void): void {
    handlePrint();
  }

  *closeSatelliteMachine(): any {
    yield this.close();
  }

  loadValues(instance: SatelliteMachine) {
    this.idValue = instance.id;
    this.nameValue = instance.name;
    this.machineCodeValue = instance.machineCode;
    this.satelliteTypeValue = instance.satelliteType;
    this.regionValue = instance.region || "";
    this.certificateExpiresAtValue = new Date(instance.certificateExpiresAt);
    this.authTokenLastFourValue = instance.authTokenLastFour;
    this.authTokenCreatedAtValue = new Date(instance.authTokenCreatedAt);
    this.authTokenRotatedAtValue = instance.authTokenRotatedAt
      ? new Date(instance.authTokenRotatedAt)
      : null;
    this.statusValue = instance.status;
    this.authorizedAtValue = instance.authorizedAt
      ? new Date(instance.authorizedAt)
      : null;
    this.authorizedBySelectedValue = instance.authorizedBy
      ? instance.authorizedBy
      : null;
    this.suspendedAtValue = instance.suspendedAt
      ? new Date(instance.suspendedAt)
      : null;
    this.suspendedBySelectedValue = instance.suspendedBy
      ? instance.suspendedBy
      : null;
    this.suspensionReasonValue = instance.suspensionReason || "";
    this.enabledValue = instance.enabled;
    this.allowedTenantIdsValue = instance.allowedTenantIds || "";
    this.proxyEnabledValue = instance.proxyEnabled;
    this.proxyPortValue = instance.proxyPort || undefined;
    this.proxyProtocolsValue = instance.proxyProtocols || "";
    this.botEnabledValue = instance.botEnabled;
    this.botMaxConcurrentValue = instance.botMaxConcurrent || undefined;
    this.workerEnabledValue = instance.workerEnabled;
    this.publicIpValue = instance.publicIp || "";
    this.locationValue = instance.location || "";
    this.countryCodeValue = instance.countryCode || "";
    this.lastHeartbeatAtValue = instance.lastHeartbeatAt
      ? new Date(instance.lastHeartbeatAt)
      : null;
    this.lastHeartbeatMetricsValue = instance.lastHeartbeatMetrics || "";
    this.connectedAtValue = instance.connectedAt
      ? new Date(instance.connectedAt)
      : null;
    this.disconnectedAtValue = instance.disconnectedAt
      ? new Date(instance.disconnectedAt)
      : null;
    this.tunnelConnectedAtValue = instance.tunnelConnectedAt
      ? new Date(instance.tunnelConnectedAt)
      : null;
    this.tasksRows = instance.tasks
      ? Object.assign(
          {},
          ...instance.tasks.map((x) => {
            const processedRow = getSatelliteTaskValues(
              x,
              this.langStore,
              false
            );
            return {
              [`__${x.id}`]: { ...processedRow, __status__: "original" },
            };
          })
        )
      : {};
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadSatelliteMachineById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
